import React, { useEffect, useMemo, useState } from "react";
import Table from "../../../components/TableV2";
import TitleButtons from "../../../components/TitleButtons";
import { generatePdfReport } from "../../../utils/pdf/generatePdfReport";
import { numeroMonetarioParaExtenso } from "../../../utils/numeroPorExtenso";
import { useEstoqueDisponivel } from "./estoqueData";
import { useNavigate } from "react-router-dom";
import TableActions from "../../../components/TableActions";

const SuprimentosEstoque: React.FC = () => {
  const navigate = useNavigate();
  const { itens, loading } = useEstoqueDisponivel();

  const headers = [
    "Posição",
    "Código",
    "NCM",
    "Nome do Produto",
    "Custo Unitário",
    "Moeda",
    "Quantidade",
    "Classificação",
  ];

  const columnWidths = [
    "10px",
    "70px",
    "70px",
    "350px",
    "100px",
    "60px",
    "10px",
    "50px",
  ];

  const [mostrarZerados, setMostrarZerados] = useState(false);
  const [revelarZeradosNosFiltros, setRevelarZeradosNosFiltros] =
    useState(false);

  const [tableFilters, setTableFilters] = useState<string[]>(
    headers.map(() => "")
  );

  const [selectedColumns, setSelectedColumns] = useState<boolean[]>(
    headers.map(() => true)
  );

  const enabledColumnIndexes = selectedColumns
    .map((checked, i) => (checked ? i : -1))
    .filter((i) => i !== -1);

  const normalizeText = (value: unknown): string =>
    String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  const formatCurrency = (
    valor: string | number | null | undefined,
    moeda?: string | null
  ): string => {
    if (!moeda) return "N/A";

    const n = typeof valor === "string" ? Number(valor) : valor ?? 0;
    if (!isFinite(n)) return "N/A";

    const symbol =
      moeda === "USD"
        ? "$"
        : moeda === "EUR"
        ? "€"
        : moeda === "BRL"
        ? "R$"
        : "";

    if (!symbol) return "N/A";

    return `${symbol} ${n.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const moedaFromItem = (p: any) =>
    p?.moeda && String(p.moeda).trim() !== "" ? String(p.moeda) : null;

  const buildRow = (p: any) => {
    const moeda = moedaFromItem(p);

    return [
      p.posicao ?? "",
      p.codigo ?? "",
      p.ncm ?? "",
      p.nomeProduto ?? "",
      formatCurrency(p.custoUnitario, moeda),
      moeda ?? "N/A",
      String(p.quantidade ?? 0),
      p.classificacao ?? "",
    ];
  };

  const allRows = useMemo(() => itens.map(buildRow), [itens]);

  const filtrosAtivos = useMemo(
    () => tableFilters.some((f) => normalizeText(f) !== ""),
    [tableFilters]
  );

  useEffect(() => {
    if (!filtrosAtivos) {
      setRevelarZeradosNosFiltros(false);
    }
  }, [filtrosAtivos]);

  const rowMatchesFilters = (
    row: (string | number | React.ReactNode)[],
    filters: string[]
  ) => {
    return row.every((cell, i) => {
      const filtro = normalizeText(filters[i] ?? "");
      if (!filtro) return true;

      const valor =
        typeof cell === "string" || typeof cell === "number"
          ? normalizeText(cell)
          : "";

      return valor.includes(filtro);
    });
  };

  const itensComRows = useMemo(
    () =>
      itens.map((item) => ({
        item,
        row: buildRow(item),
      })),
    [itens]
  );

  const itensFiltradosPelosCamposDaTabela = useMemo(() => {
    return itensComRows.filter(({ row }) =>
      rowMatchesFilters(row, tableFilters)
    );
  }, [itensComRows, tableFilters]);

  const deveMostrarZerados =
    mostrarZerados || (filtrosAtivos && revelarZeradosNosFiltros);

  const itensVisiveis = useMemo(() => {
    if (deveMostrarZerados) {
      return itensFiltradosPelosCamposDaTabela.map(({ item }) => item);
    }

    return itensFiltradosPelosCamposDaTabela
      .map(({ item }) => item)
      .filter((p) => (p.quantidade ?? 0) > 0);
  }, [itensFiltradosPelosCamposDaTabela, deveMostrarZerados]);

  const rows = useMemo(() => itensVisiveis.map(buildRow), [itensVisiveis]);

  const existeSomenteZeradoNosFiltros = useMemo(() => {
    if (!filtrosAtivos || deveMostrarZerados) return false;

    const encontrados = itensFiltradosPelosCamposDaTabela.map(
      ({ item }) => item
    );
    if (!encontrados.length) return false;

    const comSaldo = encontrados.some((p) => (p.quantidade ?? 0) > 0);
    const zerados = encontrados.some((p) => (p.quantidade ?? 0) <= 0);

    return !comSaldo && zerados;
  }, [filtrosAtivos, deveMostrarZerados, itensFiltradosPelosCamposDaTabela]);

  const quantidadeItensZeradosEncontrados = useMemo(() => {
    if (!filtrosAtivos || deveMostrarZerados) return 0;

    return itensFiltradosPelosCamposDaTabela.filter(
      ({ item }) => (item.quantidade ?? 0) <= 0
    ).length;
  }, [filtrosAtivos, deveMostrarZerados, itensFiltradosPelosCamposDaTabela]);

  const itensRelatorio = useMemo(
    () => itens.filter((p) => (p.quantidade ?? 0) > 0),
    [itens]
  );

  const rowsReport = useMemo(
    () => itensRelatorio.map(buildRow),
    [itensRelatorio]
  );

  const totalProdutosRel = useMemo(
    () => itensRelatorio.reduce((acc, p) => acc + (p.quantidade ?? 0), 0),
    [itensRelatorio]
  );

  const totaisPorMoeda = useMemo(() => {
    const acc: Record<string, number> = {};

    itensRelatorio.forEach((p) => {
      const moeda = moedaFromItem(p);
      if (!moeda) return;

      const custo = Number(p.custoUnitario ?? 0);
      const qtd = p.quantidade ?? 0;
      const subtotal = isFinite(custo * qtd) ? custo * qtd : 0;

      if (!acc[moeda]) acc[moeda] = 0;
      acc[moeda] += subtotal;
    });

    return acc;
  }, [itensRelatorio]);

  const footerLines = useMemo(() => {
    const lines: string[] = [];

    lines.push(`Total de Produtos no Estoque: ${totalProdutosRel}`);

    const entries = Object.entries(totaisPorMoeda);
    if (!entries.length) return lines;

    entries.sort(([a], [b]) => a.localeCompare(b));

    entries.forEach(([moeda, total]) => {
      const symbol =
        moeda === "USD"
          ? "$"
          : moeda === "EUR"
          ? "€"
          : moeda === "BRL"
          ? "R$"
          : "";

      const formatted = `${symbol ? symbol + " " : ""}${total.toLocaleString(
        "pt-BR",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}`;

      const extensoMoeda = numeroMonetarioParaExtenso(total, moeda);
      lines.push(
        `Valor Total em Estoque (${moeda}): ${formatted} (${extensoMoeda})`
      );
    });

    return lines;
  }, [totalProdutosRel, totaisPorMoeda]);

  const codigoIdx = headers.indexOf("Código");

  const handleToggleMostrarZerados = () => {
    setMostrarZerados((prev) => !prev);
  };

  const handleMostrarZeradosSomenteNosFiltros = () => {
    setRevelarZeradosNosFiltros(true);
  };

  return (
    <div>
      <TitleButtons
        fullBleedLine
        title={"Suprimentos - Estoque p/ Serviço & Comercial"}
      />

      <TableActions
        buttons={[
          {
            label: "Gerar Relatório",
            onClick: () =>
              generatePdfReport({
                reportTitle: "Relatório de Estoque - Suprimentos",
                fileNamePrefix: "Suprimentos-Estoque",
                subtitle: "Informações dos Produtos no Estoque",
                headers,
                rows: rowsReport,
                custoColIndex: 4,
                enabledColumns: enabledColumnIndexes,
                footerLines,
              }),
            variant: "secondary",
            dropdownOptions: headers.map((h, i) => ({
              label: h,
              checked: selectedColumns[i],
            })),
            onOptionChange: (index, checked) => {
              const updated = [...selectedColumns];
              updated[index] = checked;
              setSelectedColumns(updated);
            },
          },

          /* {
    label: "Registrar Produto / Adicionar ao Estoque",
    onClick: () =>
      navigate("/suprimentos/produtos/registrar-produto-v2"),
    variant: "primary",
  },*/
          {
            label: "Realizar Movimentação",
            onClick: () =>
              navigate(
                "/suprimentos/estoque-p-servico-e-comercial/estoque/realizar-movimentacao"
              ),
            variant: "primary",
          },
        ]}
      />

      {existeSomenteZeradoNosFiltros && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #e7cf9a",
            background: "#fff8e8",
            color: "#6b4f1f",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 500 }}>
            Você possui este item ou similar com quantidade 0.
            {quantidadeItensZeradosEncontrados > 1
              ? ` Foram encontrados ${quantidadeItensZeradosEncontrados} itens zerados.`
              : ""}{" "}
            Clique em "Mostrar Itens Zerados" para exibir.
          </div>

          <button
            type="button"
            onClick={handleMostrarZeradosSomenteNosFiltros}
            style={{
              height: 34,
              padding: "0 14px",
              border: "1px solid #c9ad66",
              background: "#ffffff",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase",
              color: "#6b4f1f",
              whiteSpace: "nowrap",
            }}
          >
            Mostrar Itens Zerados
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 16 }}>Carregando estoque…</div>
      ) : (
        <Table
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          filters={tableFilters}
          onFiltersChange={setTableFilters}
          onDoubleClick={(_viewIndex, rowData) => {
            const codigo = String(rowData[codigoIdx] ?? "");
            if (codigo) {
              navigate(
                `/suprimentos/estoque-p-servico-e-comercial/produtos/${encodeURIComponent(
                  codigo
                )}/historico`
              );
            }
          }}
          responsiveWidths={{
            1300: {
              1: "10px",
              2: "70px",
              3: "0px",
              4: "350px",
              5: "100px",
              6: "60px",
              7: "10px",
              8: "50px",
            },
          }}
        />
      )}
    </div>
  );
};

export default SuprimentosEstoque;
